import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import { SentimentBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getBranchesList } from "store/slices/branchesSlice";
import { getCustomersList } from "store/slices/customersSlice";
import { getRatingsList, resetFilters, setFilter } from "store/slices/ratingSlice";
import { formatOptionsForSelect } from "utils/formatOptions";
import { RATING_OPTIONS } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import PageHeader from "components/common/PageHeader";

const Ratings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { view, starBlack } = Icons || {};
  const dispatch = useDispatch();
  const { canAction } = usePermission();
  const { canView } = canAction("ratings");

  const {
    loading,
    totalPages,
    items: ratings,
    currentPage,
    totalCount,
    filters: { dates, rating, userId, branchId },
  } = useSelector((state) => state.Ratings);
  const { itemsList: branchesList } = useSelector((state) => state.Branches);
  const { customersList } = useSelector((state) => state.Customers);

  useEffect(() => {
    if (canView) {
      getAllData();
      dispatch(getBranchesList());
      dispatch(getCustomersList(""));
    }
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      rating: isReset ? [] : rating,
      userId: isReset ? "" : userId,
      branchId: isReset ? "" : branchId,
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };

    dispatch(getRatingsList(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const customerOptions = useMemo(
    () =>
      (customersList || []).map(({ _id, name, phone }) => ({
        name: `${phone} ${name || ""}`,
        value: _id,
      })),
    [customersList],
  );

  const filtersList = useMemo(
    () => [
      {
        key: "rating",
        placeholder: t("Select Rating"),
        type: "multiSelect",
        options: RATING_OPTIONS,
      },
      {
        key: "userId",
        placeholder: t("Select Customer"),
        type: "singleSelect",
        options: customerOptions,
        filter: true,
        filterPlaceholder: t("Search Customer"),
      },
      {
        key: "branchId",
        placeholder: t("Select Branch"),
        type: "singleSelect",
        options: formatOptionsForSelect(branchesList, language, "name"),
      },
    ],
    [t, branchesList, language, customerOptions],
  );

  const columns = useMemo(
    () => [
      {
        header: t("Customer Name"),
        key: "userId",
        render: (value) => value?.name || "-",
      },
      {
        header: t("Phone Number"),
        key: "userId",
        render: (value) => <div className="ltr_text">{value?.phone || "-"}</div>,
      },
      // {
      //   header: t("Branch Name"),
      //   key: "branchId",
      //   render: (value) =>
      //     (language === "ar" ? value?.name?.ar : value?.name?.en) || "-",
      // },
      {
        header: t("Rating"),
        key: "rating",
        render: (value) => (
          <div className="g_start gap-1">
            <img src={starBlack} width={16} height={16} alt="star" />
            {value ?? "-"}
          </div>
        ),
      },
      // {
      //   header: t("Sentiment"),
      //   key: "sentiment",
      //   render: (value) => <SentimentBadge sentiment={value} t={t} />,
      // },
      // {
      //   header: t("Comment"),
      //   key: "comment",
      //   render: (value) => (
      //     <div className="text-truncate" style={{ maxWidth: 220 }}>
      //       {value || "-"}
      //     </div>
      //   ),
      // },
      {
        header: t("Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text" style={{whiteSpace:"nowrap"}}>{formatUTCDate(value)}</div>
        ),
      },
    ],
    [t, language],
  );

  const renderActions = useCallback(
    (item) => (
      <Link to={`/ratings/rating-details?id=${item._id}`}>
        <img src={view} width={20} height={20} alt="view" />
      </Link>
    ),
    [],
  );

  if (!canView) return null;

  return (
    <div className="page-content ratings_page">
      <PageHeader title={t("Ratings")} />

      <FilterBar
        isSearchEnabled={false}
        filters={filtersList}
        selectedFilters={{ rating, userId, branchId }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
      />

      {loading && <TableShimmer rows={12} columns={7} />}

      {!loading && ratings?.length === 0 && (
        <NoDataFound title={t("No Ratings Found")} />
      )}
      {!loading && ratings?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={ratings}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default Ratings;
